import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';

// ==========================================
// COMPONENTE: PIZARRA DE FIRMA (CANVAS)
// ==========================================
function FirmaPad({ onSave, onClear }) {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    canvas.width = canvas.offsetWidth * 2;
    canvas.height = canvas.offsetHeight * 2;
    const ctx = canvas.getContext("2d");
    ctx.scale(2, 2);
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.lineWidth = 3;
    ctx.strokeStyle = "#1e293b";
  }, []);

  const getPos = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    return { x: clientX - rect.left, y: clientY - rect.top };
  };

  const startDrawing = (e) => {
    const { x, y } = getPos(e);
    const ctx = canvasRef.current.getContext("2d");
    ctx.beginPath();
    ctx.moveTo(x, y);
    setIsDrawing(true);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    const { x, y } = getPos(e);
    const ctx = canvasRef.current.getContext("2d");
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const stopDrawing = () => {
    if (!isDrawing) return;
    setIsDrawing(false);
    onSave(canvasRef.current.toDataURL("image/png"));
  };

  const handleClear = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    onClear();
  };

  return (
    <div className="flex flex-col items-center gap-3 w-full my-4">
      <div className="bg-white/60 backdrop-blur-md border border-slate-300 rounded-2xl shadow-inner overflow-hidden w-full max-w-[400px] h-[180px] relative touch-none">
        <canvas
          ref={canvasRef}
          className="w-full h-full cursor-crosshair"
          onMouseDown={startDrawing}
          onMouseMove={draw}
          onMouseUp={stopDrawing}
          onMouseLeave={stopDrawing}
          onTouchStart={startDrawing}
          onTouchMove={draw}
          onTouchEnd={stopDrawing}
        />
        <div className="absolute top-3 left-4 text-[10px] font-black text-slate-400 uppercase tracking-widest pointer-events-none select-none">
          Firma del Responsable
        </div>
      </div>
      <button 
        onClick={handleClear}
        type="button"
        className="text-[10px] uppercase tracking-wider font-black text-slate-500 hover:text-red-500 transition-colors cursor-pointer bg-white/50 px-4 py-1.5 rounded-full border border-slate-200 shadow-sm"
      >
        Limpiar Firma
      </button>
    </div>
  );
}

// ==========================================
// PORTAL PARA MODALES
// ==========================================
function SafePortalModal({ children }) {
  const elRef = useRef(null);
  if (!elRef.current && typeof document !== "undefined") {
    const div = document.createElement("div");
    div.setAttribute("data-portal-modal", "true");
    div.setAttribute("translate", "no");
    elRef.current = div;
  }
  useEffect(() => {
    const el = elRef.current;
    if (!el) return;
    document.body.appendChild(el);
    return () => { if (el.parentNode) el.parentNode.removeChild(el); };
  }, []);
  if (!elRef.current) return null;
  return ReactDOM.createPortal(children, elRef.current);
}

// ==========================================
// CATÁLOGO DE PREGUNTAS (Basado en el Excel FT-CD-03)
// ==========================================
const SECCIONES_LIMPIEZA = [
  {
    titulo: "EXTERIOR",
    icono: "🚪",
    items: [
      { id: "ext_1", texto: "Limpieza de Paredes" },
      { id: "ext_2", texto: "Limpieza de Corredores" },
      { id: "ext_3", texto: "Limpieza Puerta de ingreso" }
    ]
  },
  {
    titulo: "INTERIOR",
    icono: "🧻",
    items: [
      { id: "int_1", texto: "Pisos" },
      { id: "int_2", texto: "Paredes" },
      { id: "int_3", texto: "Techos" },
      { id: "int_4", texto: "Puertas y divisiones" },
      { id: "int_5", texto: "Espejos" },
      { id: "int_6", texto: "Lavamanos" },
      { id: "int_7", texto: "Interruptores de iluminación" },
      { id: "int_8", texto: "Sanitarios" },
      { id: "int_9", texto: "Canecas" },
      { id: "int_10", texto: "Dispensador de jabón de manos" },
      { id: "int_11", texto: "Dispensadores de papel" }
    ]
  },
  {
    titulo: "KIT DE DESINFECCIÓN",
    icono: "🪣",
    items: [
      { id: "kit_1", texto: "Jabon para manos" },
      { id: "kit_2", texto: "Escobas, cepillo, trapero y balde" },
      { id: "kit_3", texto: "Jabon para piso y desinfectante para baños" },
      { id: "kit_4", texto: "Estopa" }
    ]
  },
  {
    titulo: "ELEMENTOS DE BIOSEGURIDAD",
    icono: "😷",
    items: [
      { id: "bio_1", texto: "El personal usa tapabocas" },
      { id: "bio_2", texto: "El personal usa guantes de nitrilo" }
    ]
  }
];

// ==========================================
// FORMULARIO PRINCIPAL
// ==========================================
export default function ControlLimpiezaBanos({ usuario, onBack }) {
  const nombreUsuario = usuario?.nombres || usuario?.nombre || '';

  const [datos, setDatos] = useState({
    pdv: "",
    fecha: new Date().toISOString().split('T')[0],
    hora: new Date().toTimeString().split(' ')[0].substring(0,5),
    nombre_responsable: nombreUsuario,
    tipo_bano: "Mixto", 
    fuera_servicio: "NO", 
    observaciones: ""
  });

  const [listaSedes, setListaSedes] = useState([]);
  const [respuestas, setRespuestas] = useState({});
  const [firmaBase64, setFirmaBase64] = useState(null);
  const [customAlert, setCustomAlert] = useState({ show: false, message: "", type: "success" });

  const handleChange = (e) => setDatos({ ...datos, [e.target.name]: e.target.value });
  
  const handleCheck = (id, valor) => {
    setRespuestas(prev => ({ ...prev, [id]: valor }));
  };

  // Cargar sedes dinámicas desde la base de datos al montar
  useEffect(() => {
    const fetchSedes = async () => {
      try {
        const API_URL = process.env.REACT_APP_API_URL;
        const res = await fetch(`${API_URL}/api/sedes`);
        const data = await res.json();
        if (data.success && Array.isArray(data.sedes)) {
          setListaSedes(data.sedes);
        }
      } catch (error) {
        console.error("Error al cargar sedes maestras:", error);
      }
    };
    fetchSedes();
  }, []);

  const handleSubmit = async () => {
    if (!datos.pdv.trim()) {
      setCustomAlert({ show: true, message: "Por favor selecciona el Punto de Venta o Sede.", type: "warning" });
      return;
    }
    
    if (Object.keys(respuestas).length === 0) {
      setCustomAlert({ show: true, message: "Debes marcar el estado de al menos un elemento de limpieza.", type: "warning" });
      return;
    }

    if (!firmaBase64) {
      setCustomAlert({ show: true, message: "La firma del responsable es obligatoria.", type: "warning" });
      return;
    }

    try {
      const token = localStorage.getItem("token");
      const API_URL = process.env.REACT_APP_API_URL;

      const payload = {
        tipo_formulario: "CONTROL_LIMPIEZA_BANOS_SST",
        datosGenerales: {
          pdv: datos.pdv.trim(),
          fecha: datos.fecha,
          inspector: datos.nombre_responsable 
        },
        respuestas: {
          ...respuestas,
          hora_limpieza: datos.hora,
          tipo_bano: datos.tipo_bano,
          fuera_servicio: datos.fuera_servicio
        },
        observaciones: {
          recomendaciones_generales: datos.observaciones
        },
        puntaje_global: 100, 
        evidencias: {
          firma_evaluador: firmaBase64
        }
      };

      const res = await fetch(`${API_URL}/api/formularios/guardar`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token && { Authorization: `Bearer ${token}` })
        },
        body: JSON.stringify(payload)
      });

      const dataRes = await res.json();
      if (dataRes.success) {
        setCustomAlert({ show: true, message: "¡Control de Limpieza guardado exitosamente!", type: "success" });
        setTimeout(() => { onBack(); }, 1500);
      } else {
        setCustomAlert({ show: true, message: dataRes.message || "Error al guardar.", type: "error" });
      }
    } catch (error) {
      console.error("Error de conexión:", error);
      setCustomAlert({ show: true, message: "Error de conexión con el servidor.", type: "error" });
    }
  };

  return (
    <div className="bg-white/20 backdrop-blur-3xl rounded-2xl sm:rounded-[34px] shadow-2xl max-w-5xl w-full mx-auto border border-white/30 animate-fade-in relative h-[90vh] overflow-hidden flex flex-col">
      
      {/* CABECERA */}
      <div className="bg-transparent border-b border-white/20 px-4 py-3 sm:px-8 sm:py-5 z-40 flex items-center justify-between shrink-0">
        <div className="flex items-center gap-3">
          <button onClick={onBack} className="w-10 h-10 flex-none bg-white/60 hover:bg-white/80 text-slate-700 hover:text-red-500 flex items-center justify-center rounded-full transition-all shadow-sm border border-white/40 cursor-pointer">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M6 18L18 6M6 6l12 12"></path></svg>
          </button>
          <div>
            <h1 className="text-lg sm:text-xl font-black text-slate-800 leading-tight">Control de Limpieza y Desinfección de Baños</h1>
            <span className="bg-teal-500/20 text-teal-800 border border-teal-500/30 text-[10px] font-bold px-2 py-0.5 rounded-md backdrop-blur-sm">FT-CD-03 | V-3</span>
          </div>
        </div>
      </div>

      {/* ÁREA SCROLLABLE */}
      <div className="flex-1 overflow-y-auto p-3 sm:p-8 space-y-5 relative">
        
        {/* DATOS GENERALES */}
        <div className="bg-white/50 backdrop-blur-xl p-5 rounded-[24px] shadow-sm border border-white/60">
          <h2 className="text-sm font-black text-slate-800 uppercase tracking-tight mb-4 flex items-center gap-2">📋 Datos del Registro</h2>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
            <div className="sm:col-span-2">
              <label className="block text-[10px] font-black text-slate-700 mb-1.5 uppercase">PDV / Sede *</label>
              <select name="pdv" value={datos.pdv} onChange={handleChange} className="w-full bg-white/80 border-white focus:border-teal-400 rounded-xl px-3 py-2 text-xs font-bold text-slate-800 outline-none shadow-sm cursor-pointer">
                <option value="" disabled>-- Seleccione una sede --</option>
                {listaSedes.map(sede => (
                  <option key={sede.id} value={sede.nombre}>
                    {sede.nombre} {sede.codigo_bodega ? `(Bodega: ${sede.codigo_bodega})` : ""}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-[10px] font-black text-slate-700 mb-1.5 uppercase">Fecha *</label>
              <input type="date" name="fecha" value={datos.fecha} onChange={handleChange} className="w-full bg-white/80 border-white focus:border-teal-400 rounded-xl px-3 py-2 text-xs font-bold text-slate-800 outline-none shadow-sm" />
            </div>
            <div>
              <label className="block text-[10px] font-black text-slate-700 mb-1.5 uppercase">Hora *</label>
              <input type="time" name="hora" value={datos.hora} onChange={handleChange} className="w-full bg-white/80 border-white focus:border-teal-400 rounded-xl px-3 py-2 text-xs font-bold text-slate-800 outline-none shadow-sm" />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label className="block text-[10px] font-black text-slate-700 mb-1.5 uppercase">Responsable de Limpieza</label>
              <input type="text" name="nombre_responsable" value={datos.nombre_responsable} onChange={handleChange} className="w-full bg-white/80 border-white focus:border-teal-400 rounded-xl px-3 py-2 text-xs font-bold text-slate-800 outline-none shadow-sm" />
            </div>
            <div>
              <label className="block text-[10px] font-black text-slate-700 mb-1.5 uppercase">Tipo de Baño</label>
              <select name="tipo_bano" value={datos.tipo_bano} onChange={handleChange} className="w-full bg-white/80 border-white focus:border-teal-400 rounded-xl px-3 py-2 text-xs font-bold text-slate-800 outline-none shadow-sm">
                <option value="M">Masculino (M)</option>
                <option value="F">Femenino (F)</option>
                <option value="Mixto">Mixto</option>
              </select>
            </div>
            <div>
              <label className="block text-[10px] font-black mb-1.5 uppercase text-red-600">¿Fuera de Servicio?</label>
              <div className="flex gap-2">
                {['SI', 'NO'].map(opcion => (
                  <button 
                    key={opcion} type="button" 
                    onClick={() => setDatos({...datos, fuera_servicio: opcion})}
                    className={`flex-1 py-2 text-xs font-black rounded-xl border transition-all ${datos.fuera_servicio === opcion ? (opcion === 'SI' ? 'bg-red-500 text-white border-red-600 shadow-sm' : 'bg-emerald-500 text-white border-emerald-600 shadow-sm') : 'bg-white/60 text-slate-500 border-white'}`}
                  >
                    {opcion}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* CHECKLIST POR SECCIONES */}
        {SECCIONES_LIMPIEZA.map((seccion) => (
          <div key={seccion.titulo} className="bg-white/50 backdrop-blur-xl p-5 rounded-[24px] shadow-sm border border-white/60">
            <h2 className="text-sm font-black text-slate-800 uppercase tracking-tight mb-4 flex items-center gap-2">
              <span className="text-lg">{seccion.icono}</span> {seccion.titulo}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {seccion.items.map((item) => (
                <div key={item.id} className="flex items-center justify-between bg-white/60 p-2.5 px-4 rounded-xl border border-slate-200/60 shadow-sm">
                  <span className="text-[11px] font-bold text-slate-700">{item.texto}</span>
                  <div className="flex gap-1 bg-slate-200/50 p-1 rounded-lg">
                    <button 
                      type="button" 
                      onClick={() => handleCheck(item.id, "SI")} 
                      className={`px-3 py-1 text-[10px] font-black rounded-md transition-all ${respuestas[item.id] === "SI" ? 'bg-emerald-500 text-white shadow-sm' : 'text-slate-500 hover:bg-white/50'}`}
                    >SI</button>
                    <button 
                      type="button" 
                      onClick={() => handleCheck(item.id, "NO")} 
                      className={`px-3 py-1 text-[10px] font-black rounded-md transition-all ${respuestas[item.id] === "NO" ? 'bg-red-500 text-white shadow-sm' : 'text-slate-500 hover:bg-white/50'}`}
                    >NO</button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}

        {/* OBSERVACIONES */}
        <div className="bg-white/50 backdrop-blur-xl p-5 rounded-[24px] shadow-sm border border-white/60">
          <label className="block text-[10px] font-black text-slate-700 mb-1.5 uppercase">Condiciones y/o Recomendaciones Adicionales</label>
          <textarea name="observaciones" value={datos.observaciones} onChange={handleChange} rows="2" placeholder="Detallar si aplica..." className="w-full bg-white/80 border-white focus:border-teal-400 rounded-xl px-3 py-2 text-xs font-bold text-slate-800 outline-none shadow-sm resize-none"></textarea>
        </div>

        {/* FIRMA Y GUARDAR */}
        <div className="border-t border-white/20 pt-6 pb-2 flex flex-col items-center">
          <h3 className="text-sm font-black text-slate-800 uppercase tracking-widest mb-1">Cierre del Registro</h3>
          <p className="text-[10px] text-slate-600 font-bold mb-4">Nombres y Firma del Responsable</p>
          
          <FirmaPad onSave={setFirmaBase64} onClear={() => setFirmaBase64(null)} />

          <button onClick={handleSubmit} className="w-full max-w-sm mt-4 bg-gradient-to-r from-teal-500 to-teal-600 hover:from-teal-400 hover:to-teal-500 text-white font-black py-4 rounded-xl shadow-[0_8px_30px_rgba(20,184,166,0.3)] active:scale-95 transition-all text-sm tracking-widest uppercase cursor-pointer border border-teal-300">
            Guardar Control FT-CD-03
          </button>
        </div>
      </div>

      {/* ALERTAS */}
      {customAlert.show && (
        <SafePortalModal>
          <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-md flex justify-center items-center p-4 z-[99999] animate-fade-in">
            <div className="bg-white/50 backdrop-blur-3xl rounded-[32px] p-6 sm:p-8 max-w-sm w-full text-center shadow-[0_30px_90px_rgba(0,0,0,0.2)] border border-white/80">
              <div className={`w-20 h-20 mx-auto rounded-3xl flex items-center justify-center mb-5 shadow-sm border border-white/80 ${customAlert.type === 'success' ? 'bg-emerald-500/20 text-emerald-700' : customAlert.type === 'warning' ? 'bg-amber-500/20 text-amber-700' : 'bg-red-500/20 text-red-700'}`}>
                <span className="text-4xl">{customAlert.type === 'success' ? '✅' : customAlert.type === 'warning' ? '⚠️' : '❌'}</span>
              </div>
              <h3 className="text-xl font-extrabold text-slate-800 mb-2.5">{customAlert.type === 'success' ? '¡Éxito!' : 'Atención'}</h3>
              <p className="text-sm text-slate-700 font-medium mb-8 leading-relaxed px-2">{customAlert.message}</p>
              <button onClick={() => setCustomAlert({ ...customAlert, show: false })} className={`w-full py-3.5 rounded-2xl font-bold text-white transition-all shadow-lg cursor-pointer ${customAlert.type === 'success' ? 'bg-emerald-600 hover:bg-emerald-500' : 'bg-amber-500 hover:bg-amber-400'}`}>Entendido</button>
            </div>
          </div>
        </SafePortalModal>
      )}
    </div>
  );
}